import { useState, useCallback, useMemo } from "react";
import { send_request } from "../../../utils/Request";

export default function useSessionComponentActions({
  projectId,
  sessionId,
  isTest,
  sessionData,
  getWorkflowProgressData,
  triggerRefresh,
  refreshSessions,
}) {
  const [actionLoading, setActionLoading] = useState(false);

  const openSessionPageBySessionId = useCallback(
    (selectedSessionId) => {
      const url = `/project/logs/progress_view/${projectId}/${selectedSessionId}?test=${isTest}`;
      window.open(url, "_blank");
    },
    [projectId, isTest]
  );

  const rerunComponent = useCallback(
    (component) => {
      const componentId = component?.componentId;
      const version = sessionData?.projectVersion;
      const componentType = component?.type;

      const body = {
        inputtedPlaceholders: {},
        projectId,
        componentId,
        componentType,
        versionToRun: version,
        sessionIdsToExecuteWorkflow: [sessionId],
        isTest,
      };

      const url = `project-service/project-session/force_start_workflow`;
      setActionLoading(true);
      send_request(url, body, null, "POST")
        .then((response) => {
          getWorkflowProgressData();
          const newSessionId = response?.data?.[sessionId];
          if (
            window.location.href.includes("/project/logs/progress_view") &&
            newSessionId
          ) {
            openSessionPageBySessionId(newSessionId);
          } else if (refreshSessions) {
            refreshSessions(isTest, true);
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setActionLoading(false);
        });
    },
    [
      projectId,
      sessionId,
      isTest,
      sessionData?.projectVersion,
      getWorkflowProgressData,
      refreshSessions,
      openSessionPageBySessionId,
    ]
  );

  const retryComponent = useCallback(
    (component) => {
      const componentId = component?.componentId;
      const thread = component?.thread;

      let url = `project-service/project-session/retry_component_session/${sessionId}/${componentId}`;
      if (thread && thread.threadId && thread.threadId !== "") {
        url = `${url}/${thread.threadId}`;
      }

      setActionLoading(true);
      send_request(url, "", null, "post")
        .then(() => {
          getWorkflowProgressData();
          if (triggerRefresh) triggerRefresh();
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setActionLoading(false);
        });
    },
    [sessionId, getWorkflowProgressData, triggerRefresh]
  );

  const terminateComponent = useCallback(
    (component) => {
      const componentId = component?.componentId;
      const thread = component?.thread;

      const reqBody = { isTest };
      if (thread != null) reqBody.threadid = thread.threadId;

      const url = `project-service/project/terminate_task/${sessionId}/${componentId}`;
      setActionLoading(true);
      send_request(url, "", reqBody, "post")
        .then(() => {
          getWorkflowProgressData();
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setActionLoading(false);
        });
    },
    [sessionId, isTest, getWorkflowProgressData]
  );

  const downloadComponentJson = useCallback((component) => {
    const componentData =
      component?.data && component.data.data
        ? component.data.data
        : component?.data
        ? component.data
        : {};
    const dataStr = JSON.stringify(componentData, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const exportFileDefaultName = `component_${component?.componentId}_data.json`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();
  }, []);

  const openRerunOnLatestVersion = useCallback(
    (component) => {
      const componentId = component?.componentId;
      if (!componentId || !sessionId || !projectId) return;
      const basePath = isTest
        ? `/project/view_canvas/${projectId}`
        : `/project/production/${projectId}`;
      window.open(
        `${basePath}?force_start=${componentId}&selectedSession=${sessionId}`
      );
    },
    [projectId, sessionId, isTest]
  );

  // Memoized so SessionComponentSection (wrapped in React.memo) does not
  // re-render every time its parent panel re-renders for unrelated state.
  return useMemo(
    () => ({
      actionLoading,
      rerunComponent,
      retryComponent,
      terminateComponent,
      downloadComponentJson,
      openRerunOnLatestVersion,
    }),
    [
      actionLoading,
      rerunComponent,
      retryComponent,
      terminateComponent,
      downloadComponentJson,
      openRerunOnLatestVersion,
    ]
  );
}
